/**
 * Error handling schemas - centralized error types
 * Supports both Zod validation errors and plain string errors
 */

import { z } from 'zod';

// Basic error structure
export const FormattedErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  field: z.string().optional(),
  details: z.record(z.string(), z.any()).optional(),
});

export type FormattedError = z.infer<typeof FormattedErrorSchema>;

// Error context for debugging and logging
export const ErrorContextSchema = z.object({
  service: z.string().optional(),
  operation: z.string().optional(),
  userId: z.string().optional(),
  requestId: z.string().optional(),
  metadata: z.record(z.string(), z.any()).optional(),
});

export type ErrorContext = z.infer<typeof ErrorContextSchema>;

// Error summary - the main error response structure
export const ErrorSummarySchema = z.object({
  type: z.enum(['validation', 'authentication', 'authorization', 'business', 'system', 'network']),
  message: z.string(),
  errors: z.array(FormattedErrorSchema),
  context: ErrorContextSchema.optional(),
});

export type ErrorSummary = z.infer<typeof ErrorSummarySchema>;

// API error response schema
export const ApiErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.union([
    z.string(), // Simple string error
    z.array(z.object({ // Zod validation errors
      code: z.string(),
      message: z.string(), 
      path: z.array(z.union([z.string(), z.number()])),
      minimum: z.number().optional(),
      type: z.string().optional(),
      inclusive: z.boolean().optional(),
      exact: z.boolean().optional(),
    })),
    ErrorSummarySchema, // Formatted error summary
  ]),
  data: z.null().optional(),
});

export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>;

// Success response schema for comparison
export const ApiSuccessResponseSchema = z.object({
  success: z.literal(true),
  data: z.any(),
  error: z.null().optional(),
});

export type ApiSuccessResponse = z.infer<typeof ApiSuccessResponseSchema>;

// Combined API response
export const ApiResponseSchema = z.union([
  ApiSuccessResponseSchema,
  ApiErrorResponseSchema,
]);

export type ApiResponse = z.infer<typeof ApiResponseSchema>;